// NEW: UI_REVAMP
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { Box, Flex } from '@chakra-ui/react';
import dayjs from 'dayjs';

const CalendarNavButton = ({ direction, isDouble = false, label, onClick, isDisabled }) => {
	const Icon = direction === 'previous' ? ChevronLeftIcon : ChevronRightIcon;

	return (
		<Flex
			as='button'
			type='button'
			aria-label={label}
			onClick={onClick}
			disabled={isDisabled}
			alignItems='center'
			justifyContent='center'
			width={isDouble ? '28px' : '24px'}
			height='24px'
			color={isDisabled ? '#D9D9D9' : '#8C8C8C'}
			background='transparent'
			_disabled={{ cursor: 'not-allowed' }}
		>
			<Icon boxSize='16px' />
			{isDouble ? <Icon boxSize='16px' marginLeft='-9px' /> : null}
		</Flex>
	);
};

const DateRangeCalendarHeader = ({
	date,
	changeMonth,
	changeYear,
	decreaseMonth,
	decreaseYear,
	increaseMonth,
	increaseYear,
	minDate,
	maxDate,
}) => {
	const firstMonth = dayjs(minDate).startOf('month');
	const lastMonth = dayjs(maxDate).startOf('month');
	const monthCount = Math.max(lastMonth.diff(firstMonth, 'month') + 1, 1);
	const monthOptions = Array.from({ length: monthCount }, (_, index) =>
		firstMonth.add(index, 'month'),
	);
	const currentMonth = dayjs(date).startOf('month');
	const isOnFirstMonth = currentMonth.isSame(firstMonth, 'month');
	const isOnLastMonth = currentMonth.isSame(lastMonth, 'month');
	const isBeforeFirstMonth = currentMonth.isBefore(firstMonth, 'month');
	const isAfterLastMonth = currentMonth.isAfter(lastMonth, 'month');

	const getPreviousButtonDisabled = () => {
		if (isBeforeFirstMonth) {
			return false;
		}

		if (isOnFirstMonth) {
			return true;
		}

		return false;
	};

	const getNextButtonDisabled = () => {
		if (isAfterLastMonth) {
			return false;
		}

		if (isOnLastMonth) {
			return true;
		}

		return false;
	};

	const setCalendarMonth = (monthDate) => {
		changeYear(monthDate.year());
		changeMonth(monthDate.month());
	};

	const handleMonthChange = (event) => {
		const [year, month] = event.target.value.split('-').map(Number);

		changeYear(year);
		changeMonth(month - 1);
	};

	const handlePreviousYear = () => {
		const targetMonth = dayjs(date).startOf('month').subtract(1, 'year');

		if (targetMonth.isBefore(firstMonth, 'month')) {
			setCalendarMonth(firstMonth);
			return;
		}

		decreaseYear();
	};

	const handlePreviousMonth = () => {
		const targetMonth = dayjs(date).startOf('month').subtract(1, 'month');

		if (targetMonth.isBefore(firstMonth, 'month')) {
			setCalendarMonth(firstMonth);
			return;
		}

		decreaseMonth();
	};

	const handleNextMonth = () => {
		const targetMonth = dayjs(date).startOf('month').add(1, 'month');

		if (targetMonth.isAfter(lastMonth, 'month')) {
			setCalendarMonth(lastMonth);
			return;
		}

		increaseMonth();
	};

	const handleNextYear = () => {
		const targetMonth = dayjs(date).startOf('month').add(1, 'year');

		if (targetMonth.isAfter(lastMonth, 'month')) {
			setCalendarMonth(lastMonth);
			return;
		}

		increaseYear();
	};

	return (
		<Flex alignItems='center' justifyContent='space-between' padding='0 4px 8px'>
			<Flex alignItems='center'>
				<CalendarNavButton
					direction='previous'
					isDouble
					label='Previous year'
					onClick={handlePreviousYear}
					isDisabled={getPreviousButtonDisabled()}
				/>
				<CalendarNavButton
					direction='previous'
					label='Previous month'
					onClick={handlePreviousMonth}
					isDisabled={getPreviousButtonDisabled()}
				/>
			</Flex>

			<Box position='relative'>
				<Box
					as='select'
					aria-label='Select month and year'
					value={dayjs(date).format('YYYY-MM')}
					onChange={handleMonthChange}
					height='26px'
					padding='0 20px 0 6px'
					border='0'
					background='transparent'
					appearance='none'
					color='#000000E0'
					fontSize='14px'
					fontWeight='600'
					textAlign='center'
					cursor='pointer'
				>
					{monthOptions.map((month) => (
						<option key={month.format('YYYY-MM')} value={month.format('YYYY-MM')}>
							{month.format('MMM YYYY')}
						</option>
					))}
				</Box>
				<ChevronDownIcon
					position='absolute'
					top='50%'
					right='3px'
					boxSize='12px'
					color='#8C8C8C'
					transform='translateY(-50%)'
					pointerEvents='none'
				/>
			</Box>

			<Flex alignItems='center'>
				<CalendarNavButton
					direction='next'
					label='Next month'
					onClick={handleNextMonth}
					isDisabled={getNextButtonDisabled()}
				/>
				<CalendarNavButton
					direction='next'
					isDouble
					label='Next year'
					onClick={handleNextYear}
					isDisabled={getNextButtonDisabled()}
				/>
			</Flex>
		</Flex>
	);
};

export default DateRangeCalendarHeader;
